import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { format } from 'date-fns';

import ClientSummaryComponent from '@/components/ClientsPage/ClientSummary/ClientSummaryComponent';
import { useClients } from '@/hooks/useClients';
import { showToast } from '@/store/ducks/toasts';
import classes from './style.module.scss';

const summaryOptions = new Map([
  ['consolidated', 'CONSOLIDATED_EXTRACT'],
  ['summary', 'DETAILED_EXTRACT'],
  ['consolidated-post-paid', 'CONSOLIDATED_POST_PAID_EXTRACT'],
  ['post-paid', 'POST_PAID_EXTRACT'],
]);

const numberFormat = new Intl.NumberFormat('pt-BR', { maximumSignificantDigits: 5 });

const perPage = 15;

const ClientSummaryContainer = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [summaryType, setSummaryType] = useState('consolidated');
  const [isDropdownMenuVisible, setIsDropdownMenuVisible] = useState(false);
  const [query, setQuery] = useState({});

  const menuButtonRef = useRef(null);
  const dropdownMenuRef = useRef(null);
  const intl = useIntl();
  const { id, status } = useParams();
  const dispatch = useDispatch();

  const { useClientAccounts, useExportClientSummaryMutation } = useClients();

  const filters = useMemo(
    () => ({
      offset: (currentPage - 1) * perPage,
      limit: perPage,
      startDate: query.startDate ? format(new Date(query.startDate), 'yyyy-MM-dd') : null,
      endDate: query.endDate ? format(new Date(query.endDate), 'yyyy-MM-dd') : null,
    }),
    [currentPage, query.endDate, query.startDate]
  );

  const { data: accountsData, isLoading: loading } = useClientAccounts(id, summaryType, filters);
  const exportMutation = useExportClientSummaryMutation(id);

  const items = accountsData?.data ?? [];
  const pagination = useMemo(() => {
    if (!accountsData) return null;
    return {
      totalPages: Math.ceil(accountsData.total / perPage),
      currentPage,
      perPage,
    };
  }, [accountsData, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [summaryType, query.startDate, query.endDate]);

  useEffect(() => {
    const handleClickOutside = evt => {
      if (
        menuButtonRef.current &&
        !menuButtonRef.current.contains(evt.target) &&
        dropdownMenuRef.current &&
        !dropdownMenuRef.current.contains(evt.target)
      ) {
        setIsDropdownMenuVisible(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  });

  const setPage = page => {
    setCurrentPage(page);
  };

  const PageNumbers = () => {
    if (!pagination) return null;

    const pageNumbers = [];
    for (let i = 1; i <= pagination.totalPages; i++) {
      pageNumbers.push(i);
    }

    let offset = 4;

    if (pagination.currentPage - 4 < 1) {
      offset += 4 - (pagination.currentPage - 1);
    } else if (pagination.currentPage + 4 > pagination.totalPages) {
      offset += 4 - (pagination.totalPages - pagination.currentPage);
    }

    return pageNumbers.map(number => {
      const pageClass = number === pagination.currentPage ? classes.isActive : null;

      if (
        pagination.totalPages < 10 || // show all pages if less than 10
        number === 1 || // first page
        number === pagination.totalPages || // last page
        (number > pagination.currentPage - offset && number < pagination.currentPage + offset) // show 4 items before and after
      ) {
        return (
          <button type="button" className={`${classes.page} ${pageClass}`} key={number} onClick={() => setPage(number)}>
            {number}
          </button>
        );
      }
      if (number === 2) {
        return (
          <div className={classes.omission} key="start-dots">
            …
          </div>
        );
      }
      if (number === pagination.totalPages - 1) {
        return (
          <div className={classes.omission} key="end-dots">
            …
          </div>
        );
      }
      return null;
    });
  };

  const exportSummary = () => {
    exportMutation.mutate(
      {
        summaryType,
        startDate: query.startDate ? format(new Date(query.startDate), 'yyyy-MM-dd') : null,
        endDate: query.endDate ? format(new Date(query.endDate), 'yyyy-MM-dd') : null,
      },
      {
        onSuccess: data => {
          const title = intl.formatMessage({ id: 'summaryTitle' });
          const text = data?.client_name
            ? intl.formatMessage({ id: 'consolidatedSummaryWithClient' }, { client: data?.client_name })
            : intl.formatMessage({ id: 'consolidatedSummary' });

          dispatch(
            showToast({
              type: 'success',
              title,
              text,
              link: data?.file,
            })
          );
        },
      }
    );
  };

  const handleFilter = filter => {
    setQuery(filter);
  };

  return (
    <ClientSummaryComponent
      exportSummary={exportSummary}
      handleFilter={handleFilter}
      id={id}
      intl={intl}
      isDropdownMenuVisible={isDropdownMenuVisible}
      isExportingSummary={exportMutation.isPending}
      items={items}
      loading={loading}
      menuButtonRef={menuButtonRef}
      dropdownMenuRef={dropdownMenuRef}
      numberFormat={numberFormat}
      pageNumbers={PageNumbers}
      pagination={pagination}
      query={query}
      setIsDropdownMenuVisible={setIsDropdownMenuVisible}
      setPage={setPage}
      setSummaryType={setSummaryType}
      summaryType={summaryType}
      summaryOptions={summaryOptions}
      status={status}
    />
  );
};

export default ClientSummaryContainer;
